import {
    createContext,
    useState,
    useEffect,
    useContext,
    useRef,
} from "react";
import { useRouter } from "next/router";
import BusinessContext from "./business-context";
import { renderTables, OnCreateTable, renderRecentTables, addRecentTables, onCreateRow, renderRows, updateTable, updateRow } from "../lib/table";
import { add, set } from "date-fns";
import { generateUniqueId } from "../lib/utils";

const TableContext = createContext({
    tables: [],
    setTables: () => { },
    createTable: () => { },
    fetchTables: () => { },
    newTableId: null,
    setNewTableId: () => { },
    refreshNewTableId: () => { },
    recentTables: [],
    setRecentTables: () => { },
    fetchRecentTables: () => { },
    updateRecentTables: () => { },
    duplicateTable: () => { },
    isTableDuplicating: false,
    setIsTableDuplicating: () => { },
    editSelectedProperty: null,
    setEditSelectedProperty: () => { },
    isShareModalOpen: false,
    setIsShareModalOpen: () => { },
    toggleShareModal: () => { },
    toggleEmailNotification: () => { },
});

export const TableProvider = (props) => {
    const [tables, setTables] = useState(null);
    const [newTableId, setNewTableId] = useState(null);
    const [recentTables, setRecentTables] = useState(null);
    const router = useRouter();
    const { activeBusiness, currentUser } = useContext(BusinessContext);
    // One AI-board provisioning attempt per session; a lost race on the
    // alias (another member provisioned first) fails quietly server-side.
    const aiBoardAttempted = useRef(false);
    const [isTableDuplicating, setIsTableDuplicating] = useState(false);
    const [editSelectedProperty, setEditSelectedProperty] = useState(null);
    const [isShareModalOpen, setIsShareModalOpen] = useState(false)

    const toggleShareModal = () => { setIsShareModalOpen(!isShareModalOpen) }
    const businessId = (() => {
        const path = router.asPath || router.pathname || "";
        const parts = path.split("?")[0].split("/");
        return parts[1] || null;
    })();

    const refreshNewTableId = () => {
        setNewTableId(null);
    }

    const setDefaultVisibleKanbanColumns = (table, callBack) => {
        const firstTextColumn = table.columns.find(column => column.type === 'text');
        const firstTeammateColumn = table.columns.find(column => column.type === 'teammate');

        const metadata = {
            ...table.metadata,
            extra: {
                ...table.metadata?.extra,
                visibleColumnsOnKanban:
                    [firstTextColumn ? firstTextColumn.id : null,
                    firstTeammateColumn ? firstTeammateColumn.id : null].filter(Boolean)
            }
        };

        const updatedTable = {
            ...table,
            metadata
        };

        const onError = (json) => {
            console.error(json);
        };

        updateTable(table.uuid, updatedTable, callBack, activeBusiness, onError, router)
    }

    const makeTeamTable = (table, callBack) => {
        const payload = {
            permission: "760"
        }
        updateTable(table.uuid, payload, callBack, activeBusiness, (json) => console.error(json), router);
    }

    const createTable = (type, folderName, additionalCallback, folderPermission, translations, query, name) => {
        const tableCreationCallback = (json) => {
            { additionalCallback && additionalCallback(json); }
            {
                type !== 'folder' &&
                    router.push(`/${businessId}/t/${json.uuid}${query ? `?${query}` : ''}`);
                setNewTableId(json.uuid);
            }
            fetchTables();
            fetchRecentTables();
        }

        const callback = (json) => {
            if (type === 'kanban') {
                setDefaultVisibleKanbanColumns(json, tableCreationCallback);
            } else if (folderPermission === "team") {
                makeTeamTable(json, () => tableCreationCallback(json));
            } else {
                tableCreationCallback(json);
            }
        };

        const onError = (json) => {
            console.error(json);
        };
        let schema;
        const baseSchema = {
            "owner": 1,
            // NOT A BUG TO "FIX" WITHOUT DECIDING FIRST. These three keys
            // are not serializer fields either (see the AI board below), so
            // they are dropped and every table created here lands at 700 —
            // owner-private. That is the platform's normal state: 10,439 of
            // 10,604 tables on production are 700. Sharing is explicit,
            // through `makeTeamTable` above, which sends the honoured
            // `permission: "760"` when a folder is created as a team folder.
            // Replacing these with a `permission` value would flip EVERY new
            // table from private to business-readable for every user, which
            // is a privacy change and not a repair.
            "business_permission": 4,
            "individual_permissions": [],
            "public_permission": 0,
            "post_save_hook": 'default.notify_email',
            "metadata": {
                "extra": {}
            }
        };

        // Default translations (fallback to English)
        const t = translations || {
            columns: {
                task: "Task",
                status: "Status",
                assignedTo: "Assigned to",
                text: "Text",
                name: "Name",
                objectives: "Objectives",
                keyResults: "Key Results"
            },
            statusOptions: {
                toDo: "To Do",
                inProgress: "In Progress",
                done: "Done"
            },
            names: {
                kanban: "Kanban",
                untitledTable: "Untitled table",
                untitledDocument: "Untitled document",
                okrs: "OKRs",
                newFolder: "New Folder"
            }
        };

        if (type === 'kanban') {
            schema = {
                ...baseSchema,
                "name": name || t.names.kanban,
                "columns": [
                    {
                        "name": t.columns.task,
                        "type": "text",
                        "position": 1000.0,
                        "required": true
                    },
                    {
                        "name": t.columns.status,
                        "type": "mutable_select",
                        "options": {
                            "choices": [
                                { "ref": "to_do", "tag": t.statusOptions.toDo, "color": "#B84848" },
                                { "ref": "in_progress", "tag": t.statusOptions.inProgress, "color": "#8E6622" },
                                { "ref": "done", "tag": t.statusOptions.done, "color": "#3C821E" }
                            ]
                        },
                        "position": 2000
                    },
                    {
                        "name": name || t.columns.assignedTo,
                        "type": "teammate",
                        "position": 3000.0,
                    }
                ],
                "metadata": {
                    ...baseSchema.metadata,
                    "render_types": [{
                        "type": "board",
                        "representation_column": t.columns.task,
                        "grouping_column": t.columns.status,
                        "is_default": true
                    }],
                    "extra": {
                        "notify_email": {
                            "subject": "Task assignment on {{table_name}}",
                            "body": "You’ve been assigned a task in {{table_name}} on {{business_name}} by {{assignee_name}}. Please check it out.",
                        }
                    }
                }
            };
        } else if (type === 'blank') {
            schema = {
                ...baseSchema,
                "name": name || t.names.untitledTable,
                "columns": [{
                    "name": t.columns.text,
                    "type": "text",
                    "position": 1000.0,
                    "required": true
                }],
                "metadata": { ...baseSchema.metadata }
            };
        } else if (type === 'document') {
            schema = {
                ...baseSchema,
                "name": name || t.names.untitledDocument,
                "columns": [{
                    "name": t.columns.name,
                    "type": "text",
                    "position": 1000.0,
                    "required": true
                }],
                "metadata": {
                    ...baseSchema.metadata,
                    "render_types": [{
                        "type": "document",
                        "representation_column": "",
                        "grouping_column": "",
                        "is_default": true
                    }]
                }
            };
        } else if (type === 'okrs') {
            schema = {
                ...baseSchema,
                "name": name || t.names.okrs,
                "columns": [
                    {
                        "name": t.columns.objectives,
                        "type": "text",
                        "position": 1000.0,
                        "required": true
                    },
                    {
                        "name": t.columns.keyResults,
                        "type": "many_to_many",
                        "metadata": {
                            "datatype": "text"
                        },
                        "position": 2000.0
                    },
                ],
                "metadata": { ...baseSchema.metadata }
            };
        }
        else if (type === 'folder') {
            schema = {
                ...baseSchema,
                "name": folderName || t.names.newFolder,
                "type": "text",
                "columns": [],
                "metadata": {
                    ...baseSchema.metadata,
                    "extra": {
                        ...baseSchema.metadata?.extra,
                        "folderData": {
                            "id": generateUniqueId([], 12),
                            "is_folder": true,
                            "folder_name": "",
                            "permission": folderPermission || null
                        }
                    }
                }

            }
        }

        OnCreateTable(schema, callback, activeBusiness, onError, router);
    }

    const toggleEmailNotification = (table, callback) => {
        const isEnabled = !!table.metadata?.extra?.notify_email;
        const updatedMetadata = {
            ...table.metadata,
            extra: {
                ...table.metadata?.extra,
                notify_email: isEnabled ? null : {
                    subject: "Task assignment on {{table_name}}",
                    body: "You’ve been assigned a task in {{table_name}} on {{business_name}} by {{assignee_name}}. Please check it out.",
                }
            }
        };

        const updatedTable = {
            ...table,
            ...(isEnabled ? {} : { post_save_hook: 'default.notify_email' }),
            metadata: updatedMetadata
        };

        const onError = (json) => {
            console.error(json);
        };

        updateTable(table.uuid, updatedTable, callback, activeBusiness, onError, router);
    }


    const fetchTables = (namespace) => {
        const callback = (json) => {
            // The 'announcement' filter outlives the mechanism that made
            // those tables, deliberately. Space used to keep each person's
            // dismissed-banner ids in a hidden table of their own; that is
            // retired here in favour of the platform's announcement API,
            // whose banner already remembers dismissals in localStorage.
            // Until the existing rows are cleaned out of production, this
            // is what keeps them off somebody's table list — and nothing
            // creates a new one, so once they are gone this can go too.
            setTables(json.filter(table => table?.namespace !== 'announcement'));
            // The workspace's own tables are on screen; the AI-managed ones
            // (peg 'space') arrive as an append, so a failure there costs
            // nothing that exists today.
            fetchAITables();
        }
        const onError = (json) => {
            console.error(json);
        }
        renderTables(callback, activeBusiness, onError, router, '@', namespace)
    }

    // Tables pinned to the 'space' peg namespace: the boards Workpeg AI can
    // act on (see workpeg.json at the repo root). They are ordinary tables —
    // the peg pin is what jails the AI's server-side writes to them — but the
    // main listing filters to peg '@', so they are fetched separately and
    // appended.
    const fetchAITables = () => {
        const callback = (json) => {
            if (json.length > 0) {
                setTables((existing) => {
                    const known = new Set((existing || []).map(t => t?.uuid));
                    return [
                        ...(existing || []),
                        ...json.filter(t => t?.uuid && !known.has(t.uuid)),
                    ];
                });
            } else {
                provisionAIBoard();
            }
        };
        const onError = (json) => {
            console.error(json);
        };
        renderTables(callback, activeBusiness, onError, router, 'space');
    };

    // Creates the one designated AI task board — same self-provisioning move
    // as the announcement table, but pinned to the 'space' peg namespace and
    // aliased 'tasks' so the platform can resolve it for AI actions.
    // Requires the 'space' Peg row to exist platform-side; until it does the
    // create is refused server-side and quietly retried another session.
    const provisionAIBoard = () => {
        if (aiBoardAttempted.current) return;
        if (!currentUser || currentUser.role === 'guest') return;
        aiBoardAttempted.current = true;

        const schema = {
            "owner": 1,
            "name": "AI Tasks",
            "alias": "tasks",
            // Read+write for the whole business: AI actions run at the
            // authority of the person chatting, and the platform checks that
            // person's own write permission on this table before acting.
            //
            // "permission" IS THE FIELD, and the three-part form is not.
            // TableSerializer accepts `permission` and nothing else of that
            // family, and DRF drops unknown input keys in silence — so
            // `business_permission: 6` was accepted with a 200 and thrown
            // away, and this board was created at the model defaults: owner
            // 7, business 0, public 0. Owner-private, which is the opposite
            // of the line above. Measured on production 2026-08-21: all four
            // AI Tasks boards were 700. `makeTeamTable` above has always
            // used the honoured key; this did not.
            "permission": "760",
            "post_save_hook": 'default.notify_email',
            "columns": [
                {
                    "name": "Task",
                    "type": "text",
                    "position": 1000.0,
                    "required": true
                },
                {
                    "name": "Status",
                    "type": "mutable_select",
                    "options": {
                        "choices": [
                            { "ref": "to_do", "tag": "To Do", "color": "#B84848" },
                            { "ref": "in_progress", "tag": "In Progress", "color": "#8E6622" },
                            { "ref": "done", "tag": "Done", "color": "#3C821E" }
                        ]
                    },
                    "position": 2000
                },
                {
                    "name": "Assigned to",
                    "type": "teammate",
                    "position": 3000.0
                },
                {
                    "name": "Due",
                    "type": "datetime",
                    "position": 4000.0
                }
            ],
            "metadata": {
                "render_types": [{
                    "type": "board",
                    "representation_column": "Task",
                    "grouping_column": "Status",
                    "is_default": true
                }],
                "extra": {
                    "notify_email": {
                        "subject": "Task assignment on {{table_name}}",
                        "body": "You’ve been assigned a task in {{table_name}} on {{business_name}} by {{assignee_name}}. Please check it out.",
                    }
                }
            }
        };

        const callback = (json) => {
            setDefaultVisibleKanbanColumns(json, () => {
                setTables((existing) => {
                    const known = new Set((existing || []).map(t => t?.uuid));
                    return known.has(json.uuid)
                        ? (existing || [])
                        : [...(existing || []), json];
                });
            });
        };
        const onError = (json) => {
            console.error(json);
        };
        OnCreateTable(schema, callback, activeBusiness, onError, router,
            { "peg-namespace": "space" });
    };

    const fetchRecentTables = () => {
        const callback = (json) => {
            setRecentTables(json);
        }
        const onError = (json) => {
            console.error(json);
        }
        renderRecentTables(callback, activeBusiness, onError, router)
    }
    const updateRecentTables = (tableId, staffId) => {
        const onError = (json) => {
            console.error(json);
        }
        addRecentTables(tableId, staffId, activeBusiness, onError, router)
    }

    useEffect(() => {
        if (router.pathname.includes('/home')) {
            fetchRecentTables();
        }
        fetchTables();
    }, [activeBusiness, router]);

    const duplicateTable = (table) => {
        setIsTableDuplicating(true);
        const onError = (json) => {
            setIsTableDuplicating(false);
            console.error(json);
        };

        const tableDuplicate = {
            "name": `Duplicate of ${table.name}`,
            "owner": 1,
            // Same dropped keys as `baseSchema` above, and the same reason
            // not to change them here without a decision. If a duplicate
            // SHOULD carry its original's sharing, the value to send is
            // `table.permission` rather than a hardcoded one — a duplicate
            // inheriting a constant would be its own bug.
            "business_permission": 4,
            "individual_permissions": [],
            "public_permission": 0,
            "columns": table.columns,
            "metadata": table.metadata,
        };

        let newTableId;

        const onRowsFetched = (rows) => {
            rows?.forEach((row) => {
                const { id, permission, ...rowWithoutId } = row;
                onCreateRow(newTableId, null, rowWithoutId, activeBusiness, onError, router);
            });
            router.push(`/${businessId}/t/${newTableId}`);
        };

        const onTableCreated = (newTableJson) => {
            newTableId = newTableJson.uuid;
            renderRows(table.uuid, onRowsFetched, activeBusiness, onError, router
            );
        };

        OnCreateTable(tableDuplicate, onTableCreated, activeBusiness, onError, router);
    };



    const context = {
        tables,
        setTables,
        createTable,
        fetchTables,
        newTableId,
        setNewTableId,
        refreshNewTableId,
        recentTables,
        setRecentTables,
        fetchRecentTables,
        updateRecentTables,
        duplicateTable,
        isTableDuplicating,
        setIsTableDuplicating,
        editSelectedProperty,
        setEditSelectedProperty,
        isShareModalOpen,
        setIsShareModalOpen,
        toggleShareModal,
        toggleEmailNotification,
    };

    return (
        <TableContext.Provider value={context}>
            {props.children}
        </TableContext.Provider>
    );
};

export default TableContext;